import { MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, message, Modal } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import ThirdPartyCredentialTypeApiService from '../../../api/ThirdPartyCredentialTypeApiService';
import AuthorizedContent from '../../../components/AuthorizedContent';
import ThirdPartyCredentialTypeDetailsForm from '../../../components/forms/ThirdPartyCredentialTypeDetailsForm';
import EditCancelButton from '../../../components/buttons/EditCancelButton';
import Breadcrumbs from '../../../config/Breadcrumbs';
import Routes from '../../../config/Routes';
import FeatureFlag from '../../../consts/FeatureFlag';
import * as DeleteThirdPartyCredentialTypeHandler from '../../../handlerModels/DeleteThirdPartyCredentialTypeHandler';
import * as GetThirdPartyCredentialTypeDetailsHandler from '../../../handlerModels/GetThirdPartyCredentialTypeDetailsHandler';
import ThirdPartyCredentialTypeDTO from '../../../models/ThirdPartyCredentialTypeDTO';
import HeaderPortal from '../../../portals/HeaderPortal';
import BaseFormState from '../../../redux/bases/BaseFormState';
import HistoryUtil from '../../../utils/HistoryUtil';
import PageStayPrompt from '../../../utils/PageStayPrompt';
import ParameterUtil from '../../../utils/ParameterUtil';

interface ThirdPartyCredentialTypeDetailsPageState extends BaseFormState {
  thirdPartyCredentialTypeId: number;
  thirdPartyCredentialType?: ThirdPartyCredentialTypeDTO;
}

interface ThirdPartyCredentialTypeDetailsPageProps {
}

class ThirdPartyCredentialTypeDetailsPage extends React.Component<ThirdPartyCredentialTypeDetailsPageProps & RouteComponentProps<RouteObject>, ThirdPartyCredentialTypeDetailsPageState> {
  private readonly _thirdPartyCredentialTypeDetailsRef = React.createRef<ThirdPartyCredentialTypeDetailsForm>();

  constructor(props: ThirdPartyCredentialTypeDetailsPageProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      thirdPartyCredentialTypeId: 0
    };
  }

  componentDidMount() {
    const thirdPartyCredentialTypeId = ParameterUtil.getPathPart(this.props.match, 'id');

    if (thirdPartyCredentialTypeId == 'new') {
      this.setState({ isEditing: true, isNew: true, loading: false })
    }
    else {
      const thirdPartyCredentialTypeIdNumber = Number.parseInt(thirdPartyCredentialTypeId);
      this.loadThirdPartyCredentialType(thirdPartyCredentialTypeIdNumber);
    }
  }

  private loadThirdPartyCredentialType = (thirdPartyCredentialTypeId: number) => {
    this.setState({ thirdPartyCredentialTypeId: thirdPartyCredentialTypeId });

    ThirdPartyCredentialTypeApiService.getThirdPartyCredentialTypeDetails(thirdPartyCredentialTypeId)
      .then((results: GetThirdPartyCredentialTypeDetailsHandler.Result) => {
        if (results.thirdPartyCredentialType) {
          this.setState({ thirdPartyCredentialType: results.thirdPartyCredentialType });
        }
      }).catch(() => {
        this.setState({ error: true });
      }).finally(() => {
        this.setState({ loading: false });
      });
  }

  private deleteConfirmation = (id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this ThirdPartyCredentialType?',
      okText: 'Delete',
      onOk: () => this.deleteThirdPartyCredentialType(id)
    });
  }

  private deleteThirdPartyCredentialType = (thirdPartyCredentialTypeId: number) => {
    this.setState({ submitting: true });

    const request = DeleteThirdPartyCredentialTypeHandler.Request.create({
      thirdPartyCredentialTypeId: thirdPartyCredentialTypeId,
    });

    ThirdPartyCredentialTypeApiService.deleteThirdPartyCredentialType(request)
      .then((result: DeleteThirdPartyCredentialTypeHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          message.success('ThirdPartyCredentialType was removed.');
        }
        else {
          message.error(result?.errors.join('\n'));
        }

      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('ThirdPartyCredentialType could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToThirdPartyCredentialType();
      });
  }

  private toggleEdit = () => {
    if (this.state.isEditing) {
      this._thirdPartyCredentialTypeDetailsRef.current?.resetForm();
    }

    this.setState({ isEditing: !this.state.isEditing });
  }

  private thirdPartyCredentialTypeDetailsSaved = (thirdPartyCredentialTypeId: number) => {
    this.setState({ isEditing: false });
    if (thirdPartyCredentialTypeId != this.state.thirdPartyCredentialTypeId) {
      HistoryUtil.replace(Routes.generate(Routes.THIRD_PARTY_CREDENTIAL_TYPE_DETAILS, { id: thirdPartyCredentialTypeId }));
    }
    else {
      this.loadThirdPartyCredentialType(thirdPartyCredentialTypeId);
    }
  }

  private goToThirdPartyCredentialType = () => {
    HistoryUtil.goBack();
  }

  render() {
    const title = this.state.loading ? '...' : this.state.thirdPartyCredentialTypeId == 0 ? 'New Third Party Credential Type' : this.state.thirdPartyCredentialType?.name ?? '...';

    return (
      <Content >
        <HeaderPortal
          title={title}
          onBack={this.goToThirdPartyCredentialType}
          breadcrumbs={Breadcrumbs.thirdPartyCredentialTypeDetails(title, this.state.thirdPartyCredentialTypeId)}
          extra={this.renderMoreActions()} />
        <PageStayPrompt when={this.state.isEditing} />

        {this.renderDetailsCard()}
      </Content>
    );
  }

  renderDetailsCard() {
    const editButton = this.state.thirdPartyCredentialTypeId == 0 ?
      null :
      (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_GENDER]}>
          <EditCancelButton isEditing={this.state.isEditing} onClick={this.toggleEdit} />
        </AuthorizedContent>
      );

    return (
      <Card title="Details" extra={editButton}>
        <ThirdPartyCredentialTypeDetailsForm
          ref={this._thirdPartyCredentialTypeDetailsRef}
          thirdPartyCredentialTypeId={this.state.thirdPartyCredentialTypeId}
          isEditing={this.state.isEditing}
          isNew={this.state.isNew}
          onSave={this.thirdPartyCredentialTypeDetailsSaved} />
      </Card>
    );
  }

  renderMoreActions() {
    const menu = (
      <Menu>
        < Menu.Item title='Delete Third Party Credential Type' key={this.state.thirdPartyCredentialTypeId} onClick={() => this.deleteConfirmation(this.state.thirdPartyCredentialTypeId)}  >
          Delete Third Party Credential Type
        </Menu.Item>
      </Menu>
    );
    return (
      <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_GENDER]}>
        <Dropdown overlay={menu}>
          <Button size='large' type='link' >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </AuthorizedContent>
    );
  }
}

export default withRouter(ThirdPartyCredentialTypeDetailsPage);
